import React, { useEffect } from 'react';
import { Link } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../hooks/redux.hooks';
import { useFeatureFlag } from '../../hooks/custom.hooks';
import { FeatureFlags } from '../../constants/feature-flags.constants';
import { CHAT_SCRIPT_ID, CHAT_SCRIPT_SRC } from '../../constants/chat.constants';
import { setScriptLoaded, setChatError } from '../../state/reducers/chatbot.reducers';
import { getChatbotConfig } from '../../services/chatbot.service';

// Original ChatBot component
const ChatBot: React.FC = () => {
	const dispatch = useAppDispatch();

	const { config: chatConfig, scriptLoaded, error } = useAppSelector((state) => state.chatbot);

	// Load script and set up error handler
	useEffect(() => {
		// If script already exists, mark as loaded
		if (document.getElementById(CHAT_SCRIPT_ID)) {
			dispatch(setScriptLoaded(true));
			return;
		}

		// Set up error handler
		const globalErrorHandler = (event: ErrorEvent) => {
			if (event.message?.includes('Script error')) {
				console.warn(
					'Third-party (Commbox Chat) script error caught and silenced:',
					event.message,
					event.error
				);
				event.preventDefault();
				event.stopPropagation();
			}
		};

		window.addEventListener('error', globalErrorHandler, true);

		// Create and append script
		const script = document.createElement('script');
		script.id = CHAT_SCRIPT_ID;
		script.src = CHAT_SCRIPT_SRC;
		script.async = true;

		script.onload = () => dispatch(setScriptLoaded(true));
		script.onerror = (event) => {
			console.error('Failed to load Commbox script:', event);
			dispatch(setChatError('Chat service script failed to load.'));
		};

		document.body.appendChild(script);

		// Cleanup
		return () => {
			window.removeEventListener('error', globalErrorHandler, true);
			document.getElementById(CHAT_SCRIPT_ID)?.remove();
		};
	}, []);

	// Fetch config and initialize chat
	useEffect(() => {
		// Fetch chat config if conditions are met
		if (scriptLoaded) {
			dispatch(getChatbotConfig());
		}
	}, [scriptLoaded]);

	useEffect(() => {
		if (!chatConfig) {
			return;
		}

		// Initialize chat if conditions are met
		if (!error && window.BY && typeof window.BY.chat === 'function') {
			const timerId = setTimeout(() => {
				try {
					console.log('Initializing Commbox chat with config:', chatConfig);
					window.BY!.chat({
						baseDomain: chatConfig.baseDomain,
						appsSubDomain: chatConfig.appsSubDomain,
						streamId: chatConfig.streamId,
						ta: chatConfig.ta,
					});
				} catch (initError) {
					dispatch(setChatError('An error occurred while starting the chat service.'));
				}
			}, 300);

			return () => clearTimeout(timerId);
		}
	}, [chatConfig]);

	return null;
};

// WhatsApp Button component
const WhatsAppButton: React.FC = () => {
	return (
		<Link
			href="https://wa.me/972523242220?text=שלום,%20אני%20מעוניין%20לקבל%20את%20שירות%20Open.%20אנא%20שלח%20הודעה%20זו%20כמו%20שהיא%20כדי%20להתחיל."
			target="_blank"
			title="דברו איתנו"
			sx={{
				position: 'fixed',
				bottom: '20px',
				right: '20px',
				zIndex: 999,
				backgroundColor: '#25D366',
				borderRadius: '50%',
				padding: '10px',
				boxShadow: '2px 2px 5px rgba(0,0,0,0.3)',
				textDecoration: 'none',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				'&:hover': {
					backgroundColor: '#128C7E',
				},
			}}
		>
			<img
				src="https://cdn-icons-png.flaticon.com/512/220/220236.png"
				alt="Chat on WhatsApp"
				style={{ height: '50px', width: '50px' }}
			/>
		</Link>
	);
};

// Chat selector component that chooses between the two
const Chat: React.FC = () => {
	const isWhatsAppButtonEnabled = useFeatureFlag(FeatureFlags.WhatsAppButtonEnabled);

	return isWhatsAppButtonEnabled ? <WhatsAppButton /> : <ChatBot />;
};

export default Chat;
